/** Spectral scope — real-time frequency spectrum visualization via AnalyserNode. */

import { useCallback, useEffect, useRef, useState } from "react";

/** Valid FFT sizes for the spectral scope. */
export type FftSize = 1024 | 2048 | 4096;

/** Named quality levels mapped to FFT sizes. */
export const FFT_SIZE_LABELS: Record<FftSize, string> = {
	1024: "Low",
	2048: "Medium",
	4096: "High",
} as const;

/** Ordered FFT size options. */
export const FFT_SIZES: readonly FftSize[] = [1024, 2048, 4096] as const;

/** Props for the SpectralScope component. */
export type SpectralScopeProps = {
	readonly analyser: AnalyserNode | null;
};

/** Number of horizontal grid lines in the spectral display. */
const GRID_LINE_COUNT = 4 as const;

export function SpectralScope({ analyser }: SpectralScopeProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const animationRef = useRef<number>(0);
	const [fftSize, setFftSize] = useState<FftSize>(2048);

	// Apply fftSize to the analyser node whenever it changes
	useEffect(() => {
		if (analyser) {
			analyser.fftSize = fftSize;
		}
	}, [analyser, fftSize]);

	const draw = useCallback(() => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");
		if (!canvas || !ctx || !analyser) {
			animationRef.current = requestAnimationFrame(draw);
			return;
		}

		const bufferLength = analyser.frequencyBinCount;
		const dataArray = new Uint8Array(bufferLength);
		analyser.getByteFrequencyData(dataArray);

		const dpr = window.devicePixelRatio || 1;

		// Scale canvas for retina
		if (canvas.width !== canvas.clientWidth * dpr) {
			canvas.width = canvas.clientWidth * dpr;
			canvas.height = canvas.clientHeight * dpr;
		}

		const width = canvas.width;
		const height = canvas.height;

		ctx.fillStyle = "#0a0014";
		ctx.fillRect(0, 0, width, height);

		// Draw horizontal grid lines
		ctx.strokeStyle = "rgba(255, 255, 255, 0.06)";
		ctx.lineWidth = 1;
		for (let i = 1; i < GRID_LINE_COUNT; i++) {
			const y = (height * i) / 4;
			ctx.beginPath();
			ctx.moveTo(0, y);
			ctx.lineTo(width, y);
			ctx.stroke();
		}

		// Draw frequency bars
		const barWidth = width / bufferLength;

		for (let i = 0; i < bufferLength; i++) {
			const value = dataArray[i] as number;
			const barHeight = (value / 255) * height;

			// Color gradient: purple (low freq) → cyan (high freq)
			const hue = 270 - (i / bufferLength) * 120;
			ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.85)`;
			ctx.fillRect(i * barWidth, height - barHeight, barWidth, barHeight);
		}

		animationRef.current = requestAnimationFrame(draw);
	}, [analyser]);

	useEffect(() => {
		animationRef.current = requestAnimationFrame(draw);
		return () => cancelAnimationFrame(animationRef.current);
	}, [draw]);

	return (
		<div className="scope-container spectral-scope-container">
			<div className="spectral-scope-controls">
				<span className="spectral-scope-label">Spectrum</span>
				<div className="spectral-fft-buttons">
					{FFT_SIZES.map((size) => (
						<button
							key={size}
							type="button"
							className={`spectral-fft-btn ${fftSize === size ? "active" : ""}`}
							onClick={() => setFftSize(size)}
						>
							{FFT_SIZE_LABELS[size]}
						</button>
					))}
				</div>
			</div>
			<canvas ref={canvasRef} className="spectral-scope-canvas" />
		</div>
	);
}
